/** The order the inbox table lists its rows in. */
export type InboxSort = "newest" | "oldest" | "opportunity";

/** The choices offered by the Sort menu, in display order. */
export const INBOX_SORT_OPTIONS: { value: InboxSort; label: string }[] = [
  { value: "newest", label: "Newest first" },
  { value: "oldest", label: "Oldest first" },
  { value: "opportunity", label: "Opportunity" },
];

/** The fields the inbox table sorts on. */
export interface InboxSortKeys {
  opportunityName: string | null;
  updatedAt: Date;
}

/**
 * Compares two rows' sort keys for the given order. Newest and oldest order by
 * last update. Opportunity groups rows by opportunity name so rows for the same
 * opportunity sit together, newest first within a group, with rows that have no
 * opportunity last.
 * @param a - The first row's sort keys
 * @param b - The second row's sort keys
 * @param sort - The order to compare for
 * @returns A negative number when a sorts first, positive when b does, 0 when equal
 */
export function compareInboxSortKeys(
  a: InboxSortKeys,
  b: InboxSortKeys,
  sort: InboxSort,
): number {
  switch (sort) {
    case "newest":
      return compareNewestFirst(a, b);
    case "oldest":
      return a.updatedAt.getTime() - b.updatedAt.getTime();
    case "opportunity": {
      const opportunityOrder = compareOpportunityNames(
        a.opportunityName,
        b.opportunityName,
      );
      return opportunityOrder !== 0
        ? opportunityOrder
        : compareNewestFirst(a, b);
    }
  }
}

/**
 * Orders the more recently updated row first.
 * @param a - The first row's sort keys
 * @param b - The second row's sort keys
 * @returns A negative number when a is newer, positive when b is, 0 when equal
 */
function compareNewestFirst(a: InboxSortKeys, b: InboxSortKeys): number {
  return b.updatedAt.getTime() - a.updatedAt.getTime();
}

/**
 * Compares two opportunity names case-insensitively, ordering missing names last.
 * @param a - The first opportunity name, or null when the row has no opportunity
 * @param b - The second opportunity name, or null when the row has no opportunity
 * @returns A negative number when a sorts first, positive when b does, 0 when equal
 */
function compareOpportunityNames(a: string | null, b: string | null): number {
  if (a === null && b === null) {
    return 0;
  }
  if (a === null) {
    return 1;
  }
  if (b === null) {
    return -1;
  }
  return a.localeCompare(b, undefined, { sensitivity: "base" });
}
